import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminAPI, authAPI, usersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';
import { format } from 'date-fns';

// ── Reusable stat card ─────────────────────────────────────────
function StatCard({ icon, value, label, color }) {
  return (
    <div className="stat-card">
      <div className="stat-icon" style={{ background: color + '22' }}>
        <span style={{ color, fontSize: 20 }}>{icon}</span>
      </div>
      <div className="stat-value">{value ?? '…'}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function Badge({ status }) {
  const map = { active: 'badge-success', pending: 'badge-warning', inactive: 'badge-secondary', rejected: 'badge-danger' };
  return <span className={`badge ${map[status] || 'badge-secondary'}`}>{status}</span>;
}

// ── Change Password Modal (used by admin) ──────────────────────
function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const getStrength = pwd => {
    if (!pwd) return 0;
    let s = 0;
    if (pwd.length >= 8) s++;
    if (/[A-Z]/.test(pwd)) s++;
    if (/[0-9]/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    return s;
  };
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['', '#ef4444', '#f59e0b', '#3b82f6', '#10b981'];
  const pwStrength = getStrength(form.newPassword);

  const validate = () => {
    const errs = {};
    if (!form.currentPassword) errs.currentPassword = 'Current password required';
    if (!form.newPassword || form.newPassword.length < 8) errs.newPassword = 'Minimum 8 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(form.newPassword))
      errs.newPassword = 'Need uppercase, lowercase and number';
    if (form.newPassword !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await usersAPI.changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      toast.success('✅ Admin password changed successfully!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password.');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <div className="modal-title">🔒 Change Admin Password</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info" style={{ fontSize: 12, marginBottom: 16 }}>
            Password must be 8+ characters with uppercase, lowercase, and a number.
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className={`form-input${errors.currentPassword ? ' error' : ''}`}
                value={form.currentPassword}
                onChange={e => { setForm(f => ({ ...f, currentPassword: e.target.value })); setErrors(er => ({ ...er, currentPassword: '' })); }}
                placeholder="Enter your current password"
                autoComplete="current-password"
              />
              {errors.currentPassword && <span className="form-error">{errors.currentPassword}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className={`form-input${errors.newPassword ? ' error' : ''}`}
                value={form.newPassword}
                onChange={e => { setForm(f => ({ ...f, newPassword: e.target.value })); setErrors(er => ({ ...er, newPassword: '' })); }}
                placeholder="Enter new password"
                autoComplete="new-password"
              />
              {errors.newPassword && <span className="form-error">{errors.newPassword}</span>}
              {form.newPassword && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 3, background: i <= pwStrength ? strengthColors[pwStrength] : 'var(--border)', transition: 'background 0.2s' }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: strengthColors[pwStrength], fontWeight: 600 }}>
                    {strengthLabels[pwStrength]}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className={`form-input${errors.confirmPassword ? ' error' : ''}`}
                value={form.confirmPassword}
                onChange={e => { setForm(f => ({ ...f, confirmPassword: e.target.value })); setErrors(er => ({ ...er, confirmPassword: '' })); }}
                placeholder="Repeat new password"
                autoComplete="new-password"
              />
              {errors.confirmPassword && <span className="form-error">{errors.confirmPassword}</span>}
              {form.confirmPassword && form.newPassword === form.confirmPassword && form.newPassword && (
                <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 3, fontWeight: 600 }}>✓ Passwords match</div>
              )}
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Changing…' : '🔒 Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Main Admin Panel ───────────────────────────────────────────
export default function AdminPanel() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [adminLoggedIn, setAdminLoggedIn] = useState(!!user && user.role === 'admin');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);   // Fix 1

  const loadStats = useCallback(async () => {
    try {
      const res = await adminAPI.getDashboard();
      setStats(res.data.data);
      setPendingCount(res.data.data.pendingApprovals);
    } catch {}
  }, []);

  useEffect(() => { if (adminLoggedIn) loadStats(); }, [adminLoggedIn, loadStats]);

  const handleAdminLogin = async e => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await authAPI.adminLogin(loginForm);
      login(res.data.data.user, res.data.data.token);
      setAdminLoggedIn(true);
      toast.success('Admin login successful!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid admin credentials.');
    } finally { setLoginLoading(false); }
  };

  if (!adminLoggedIn) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card">
          <div className="auth-brand">
            <div className="auth-brand-icon">🛡️</div>
            <div>
              <div className="auth-brand-name">Admin Panel</div>
              <div className="auth-brand-sub">TeamChat Administration</div>
            </div>
          </div>
          <form onSubmit={handleAdminLogin}>
            <div className="form-group">
              <label className="form-label">Admin Email</label>
              <input type="email" className="form-input" placeholder="admin@company.com"
                value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Admin Password</label>
              <input type="password" className="form-input" placeholder="••••••••"
                value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} />
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={loginLoading}>
              {loginLoading ? 'Signing in…' : '🛡️ Sign In as Admin'}
            </button>
          </form>
          <div className="auth-footer"><a href="/login">← Employee Login</a></div>
        </div>
      </div>
    );
  }

  const pages = {
    dashboard:     <DashboardPage stats={stats} onReload={loadStats} />,
    registrations: <RegistrationsPage onCountChange={setPendingCount} />,
    employees:     <EmployeesPage />
  };

  return (
    <>
      <div className="admin-layout">
        <div className="admin-sidebar">
          <div className="admin-sidebar-brand">
            <span className="admin-sidebar-icon">🛡️</span>
            <div className="admin-sidebar-title">Admin Panel</div>
            <div className="admin-sidebar-sub">TeamChat Administration</div>
          </div>

          <nav className="admin-nav">
            {[
              { key: 'dashboard',     icon: '📊', label: 'Dashboard' },
              { key: 'registrations', icon: '📋', label: 'Registrations', badge: pendingCount },
              { key: 'employees',     icon: '👥', label: 'Employees' }
            ].map(item => (
              <div key={item.key}
                className={`admin-nav-item${page === item.key ? ' active' : ''}`}
                onClick={() => setPage(item.key)}>
                <span className="nav-icon">{item.icon}</span>
                <span>{item.label}</span>
                {item.badge > 0 && <span className="admin-nav-badge">{item.badge}</span>}
              </div>
            ))}
          </nav>

          {/* Fix 1: Admin footer with Change Password button clearly visible */}
          <div className="admin-sidebar-footer">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Avatar user={user} size="sm" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{user?.name}</div>
                <div style={{ fontSize: 11, color: 'var(--sidebar-text)' }}>Administrator</div>
              </div>
            </div>
            {/* Two clear action buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button
                onClick={() => setShowChangePw(true)}
                style={{
                  width: '100%', height: 34,
                  background: 'rgba(88,101,242,.18)',
                  border: '1px solid rgba(88,101,242,.35)',
                  borderRadius: 6,
                  color: '#a8b4ff', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 6
                }}>
                🔒 Change Password
              </button>
              <button
                onClick={() => { logout(); navigate('/login'); }}
                style={{
                  width: '100%', height: 34,
                  background: 'rgba(239,68,68,.12)',
                  border: '1px solid rgba(239,68,68,.25)',
                  borderRadius: 6,
                  color: '#fca5a5', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 6
                }}>
                ↩ Logout
              </button>
            </div>
          </div>
        </div>

        <div className="admin-content">
          <div className="admin-inner">{pages[page]}</div>
        </div>
      </div>

      {/* Fix 1: Change password modal for admin */}
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </>
  );
}

// ── Dashboard ──────────────────────────────────────────────────
function DashboardPage({ stats, onReload }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 className="admin-page-title" style={{ margin: 0 }}>Dashboard</h1>
        <button className="btn btn-ghost btn-sm" onClick={onReload}>🔄 Refresh</button>
      </div>
      <div className="stats-grid">
        <StatCard icon="👥" value={stats?.totalEmployees}      label="Total Employees"   color="#5865f2" />
        <StatCard icon="⏳" value={stats?.pendingApprovals}    label="Pending Approvals" color="#f59e0b" />
        <StatCard icon="✅" value={stats?.activeEmployees}     label="Active Employees"  color="#10b981" />
        <StatCard icon="🔴" value={stats?.inactiveEmployees}   label="Inactive Accounts" color="#ef4444" />
        <StatCard icon="🟢" value={stats?.onlineNow}           label="Online Right Now"  color="#06b6d4" />
        <StatCard icon="🆕" value={stats?.recentRegistrations} label="New This Week"     color="#8b5cf6" />
      </div>
      {stats?.pendingApprovals > 0 && (
        <div className="alert alert-warn">
          ⚠️ <strong>{stats.pendingApprovals} registration{stats.pendingApprovals > 1 ? 's' : ''}</strong> awaiting approval.
        </div>
      )}
      <div className="alert alert-info" style={{ marginTop: 12 }}>
        🔒 Employee chat messages are private and not visible to admin.
      </div>
    </div>
  );
}

// ── Registrations ──────────────────────────────────────────────
function RegistrationsPage({ onCountChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getRegistrations({ status: filter, limit: 50 });
      setUsers(res.data.data.users);
      if (filter === 'pending') onCountChange(res.data.data.total);
    } catch { toast.error('Failed to load.'); }
    finally { setLoading(false); }
  }, [filter, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id, name) => {
    setActionLoading(a => ({ ...a, [id]: 'approve' }));
    try { await adminAPI.approveUser(id); toast.success(`${name} approved!`); load(); }
    catch { toast.error('Failed.'); }
    finally { setActionLoading(a => ({ ...a, [id]: null })); }
  };

  const reject = async (id, name) => {
    const reason = window.prompt(`Reason for rejecting ${name} (optional):`);
    if (reason === null) return;
    setActionLoading(a => ({ ...a, [id]: 'reject' }));
    try { await adminAPI.rejectUser(id, reason); toast.success(`${name} rejected.`); load(); }
    catch { toast.error('Failed.'); }
    finally { setActionLoading(a => ({ ...a, [id]: null })); }
  };

  return (
    <div>
      <h1 className="admin-page-title">Registration Requests</h1>
      <div className="filter-bar">
        {['pending','active','rejected','all'].map(s => (
          <button key={s} className={`btn ${filter === s ? 'btn-primary' : 'btn-ghost'} btn-sm`}
            onClick={() => setFilter(s)} style={{ textTransform: 'capitalize' }}>{s}</button>
        ))}
      </div>
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Employee ID</th>
                <th>Registered</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32 }}>Loading…</td></tr>
                : users.length === 0
                ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>No records.</td></tr>
                : users.map(u => (
                  <tr key={u._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar user={u} size="sm" />
                        <div>
                          <div style={{ fontWeight: 500 }}>{u.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><code style={{ fontSize: 12 }}>{u.employeeId}</code></td>
                    <td style={{ fontSize: 12 }}>{format(new Date(u.createdAt), 'dd MMM yyyy HH:mm')}</td>
                    <td><Badge status={u.status} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {u.status === 'pending' && (
                          <>
                            <button className="btn btn-success btn-sm" disabled={!!actionLoading[u._id]}
                              onClick={() => approve(u._id, u.name)}>
                              {actionLoading[u._id] === 'approve' ? '…' : '✓ Approve'}
                            </button>
                            <button className="btn btn-danger btn-sm" disabled={!!actionLoading[u._id]}
                              onClick={() => reject(u._id, u.name)}>
                              {actionLoading[u._id] === 'reject' ? '…' : '✗ Reject'}
                            </button>
                          </>
                        )}
                        {u.status === 'rejected' && (
                          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{u.rejectionReason || 'No reason'}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Employees ──────────────────────────────────────────────────
function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const LIMIT = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getEmployees({ search, status: statusFilter, page, limit: LIMIT });
      setEmployees(res.data.data.employees);
      setTotal(res.data.data.total);
      setPages(res.data.data.pages);
    } catch { toast.error('Failed to load employees.'); }
    finally { setLoading(false); }
  }, [search, statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const toggle = async emp => {
    try { const res = await adminAPI.toggleEmployee(emp._id); toast.success(res.data.message); load(); }
    catch { toast.error('Action failed.'); }
  };

  const del = async emp => {
    if (!window.confirm(`Permanently delete ${emp.name}? This cannot be undone.`)) return;
    try { await adminAPI.deleteEmployee(emp._id); toast.success(`${emp.name} deleted.`); load(); }
    catch { toast.error('Delete failed.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 className="admin-page-title" style={{ margin: 0 }}>Employees ({total})</h1>
      </div>
      <div className="filter-bar">
        <input placeholder="Search name, email or ID…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>ID</th>
                <th>Status</th>
                <th>Presence</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32 }}>Loading…</td></tr>
                : employees.length === 0
                ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>No employees found.</td></tr>
                : employees.map(emp => (
                  <tr key={emp._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar user={emp} size="sm" />
                        <div>
                          <div style={{ fontWeight: 500 }}>{emp.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{emp.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><code style={{ fontSize: 12 }}>{emp.employeeId}</code></td>
                    <td><Badge status={emp.status} /></td>
                    <td>
                      <span style={{ fontSize: 12, fontWeight: 700, color: emp.isOnline ? 'var(--online)' : 'var(--offline)' }}>
                        ● {emp.isOnline ? 'Online' : 'Offline'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>{format(new Date(emp.createdAt), 'dd MMM yyyy')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className={`btn btn-sm ${emp.status === 'active' ? 'btn-danger' : 'btn-success'}`}
                          onClick={() => toggle(emp)}>
                          {emp.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => del(emp)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="pagination" style={{ padding: '12px 20px' }}>
            <span>Showing {employees.length} of {total}</span>
            <div className="pagination-btns">
              <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span style={{ padding: '0 8px', alignSelf: 'center', fontSize: 13 }}>{page} / {pages}</span>
              <button className="btn btn-ghost btn-sm" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
